import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Paperclip, ImageIcon } from 'lucide-react';
import { brandConfig } from '../../config/brands';
import { useProducts } from '../../features/products/hooks';
import { type VideoModelType } from '../../components/generation/state/useGenerationState';
import { useGenerationAssets } from '../../components/generation/state/assetsContext';
import { useGenerationSettings } from '../../components/generation/state/settingsContext';
import { useGenerationCredits, useCreditBadgeText } from '../../components/generation/hooks/useGenerationCredits';
import { useGenerationModels } from '../../features/generation-models/hooks';
import { useMaterialsPanelStore } from '../../stores/materialsPanelStore';
import { useIsMobile } from '../../hooks/useIsMobile';
import { usePartnerBranding } from '../../hooks/usePartnerBranding';
import { renderAccentText } from '../../utils/renderAccentText';
import { SelectedAssetsPanel } from '../../components/generation/assets/SelectedAssetsPanel';
import { MediaRequirementsAccordion } from '../../components/generation/MediaRequirementsAccordion';
import { VideoGenerationSettings } from '../../components/generation/scenario/VideoGenerationSettings';
import { VideoModelFamilySelector } from '../../components/generation/common/VideoModelFamilySelector';
import { HomeHeroMenu } from '../../components/home/HomeHeroMenu';
import MaterialsFloatingOpenButton from '../../components/panels/MaterialsFloatingOpenButton';
import { Tooltip } from '../../components/common/Tooltip';
import { useUploadTemporaryMaterial } from '../../features/materials/hooks';
import { useNavigationGuide, navigationGuideEnabled } from '../../features/navigation-guide/useNavigationGuide';
import { useUserProfile } from '../../hooks/useUserProfile';
import { getPendingSurveyQuestion } from '../../components/survey/pendingSurveyQuestion';
import { useSurveyModalStore } from '../../stores/surveyModalStore';
import { resolveUploadedGenerationAsset } from '../../features/materials/uploadedAsset';
import { rasterErrorMessage } from '../../features/materials/ensureRasterAsset';
import { isSvgFile, svgUploadPrecheckError } from '../../features/materials/svgLayerUpload';
import { useSvgLayerUploadGate } from '../../features/materials/useSvgLayerUploadAllowed';
import { useToast } from '../../hooks/useToast';
import './HypeAppHomePage.css';

type GenerationType = 'image' | 'video';

const HOME_PROMPT_STORAGE_KEY = 'hypeapp_home_prompt';
const HOME_DETAILS_OPEN_STORAGE_KEY = 'hypeapp_home_details_open';

export default function HypeAppHomePage() {
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState<string>(() => localStorage.getItem(HOME_PROMPT_STORAGE_KEY) ?? '');

  // 入力内容を localStorage に保持（リロード・他ページ遷移後もトップに戻ると復元される）
  useEffect(() => {
    localStorage.setItem(HOME_PROMPT_STORAGE_KEY, prompt);
  }, [prompt]);
  const [generationType, setGenerationType] = useState<GenerationType>('image');
  const [showMediaRequirements, setShowMediaRequirements] = useState<boolean>(
    () => localStorage.getItem(HOME_DETAILS_OPEN_STORAGE_KEY) === '1',
  );

  // 詳細設定の開閉状態を localStorage に保持
  useEffect(() => {
    localStorage.setItem(HOME_DETAILS_OPEN_STORAGE_KEY, showMediaRequirements ? '1' : '0');
  }, [showMediaRequirements]);
  const promptRef = useRef<HTMLTextAreaElement>(null);

  // 初回マウント時にプロンプト入力欄へフォーカス
  useEffect(() => {
    promptRef.current?.focus();
  }, []);

  const { selectedAssets, clearAssets, reorderAssets, addAsset } = useGenerationAssets();
  const {
    videoSettings,
    setVideoSettings,
    setVideoGenerationMode,
    setPendingEdit,
    setPendingVideoGeneration,
  } = useGenerationSettings();

  const uploadMutation = useUploadTemporaryMaterial();
  // SVG は ドロップされたときに availability を確認する (ホームでは eager に取りに行かない)
  const svgGate = useSvgLayerUploadGate();
  const toast = useToast();
  const [isDragOver, setIsDragOver] = useState(false);


  const handleCardDragOver = (e: React.DragEvent) => {
    if (!Array.from(e.dataTransfer.types).includes('Files')) return;
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleCardDragLeave = (e: React.DragEvent) => {
    // カード外に出た場合のみ解除（子要素遷移で発火する leave を無視）
    if (e.currentTarget === e.target) {
      setIsDragOver(false);
    }
  };

  const handleCardDrop = async (e: React.DragEvent) => {
    if (!Array.from(e.dataTransfer.types).includes('Files')) return;
    e.preventDefault();
    setIsDragOver(false);
    // SVG は image/* に含まれるが、レイヤー素材 (モーショングラフィックス) の入稿経路なので
    // タブが見える利用者だけ受け、それ以外は「対応していない形式」で弾く (svgLayerUpload.ts)
    const dropped = Array.from(e.dataTransfer.files);
    const allowSvgUpload = await svgGate.resolveAllowSvg(dropped);
    const files = dropped.filter((f) => {
      if (!(f.type.startsWith('image/') || f.type.startsWith('video/') || isSvgFile(f))) return false;
      const precheck = svgUploadPrecheckError(f, { allowSvg: allowSvgUpload });
      if (precheck) {
        toast.error(`アップロードエラー: ${precheck}`);
        return false;
      }
      return true;
    });
    if (files.length === 0) return;
    const results = await Promise.all(files.map((file) => uploadMutation.mutateAsync({ file })));
    for (const material of results) {
      try {
        addAsset(await resolveUploadedGenerationAsset(material));
      } catch (error) {
        // 非ラスタ原本 (SVG) のラスタ版が取得できなかった。登録は済んでいるので理由だけ出す
        console.error('ラスタ版の取得に失敗:', error);
        toast.error(rasterErrorMessage(error));
      }
    }
  };

  // モバイル: カメラ撮影 → 一時素材としてアップロードし、選択中素材へ自動追加
  // (素材パネル RightPanelWrapper のカメラ動線と同じ仕組みを流用)
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const handleCameraCapture = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const material = await uploadMutation.mutateAsync({ file });
    addAsset(await resolveUploadedGenerationAsset(material));
    // 同じ写真を撮り直しても onChange が発火するよう input をリセット
    if (cameraInputRef.current) {
      cameraInputRef.current.value = '';
    }
  };

  const { open: openPanel, close: closePanel, openWithProductFilter } = useMaterialsPanelStore();
  const { homeHeroTitle, homeHeroSubtitle } = usePartnerBranding(true);
  const isMobile = useIsMobile();
  const { data: productsData } = useProducts({ limit: 8, sort: 'updated_at', order: 'desc' });
  const recentProducts = productsData?.products ?? [];

  // 他ページから戻ってきた際にパネルを閉じておく
  useEffect(() => {
    closePanel();
  }, [closePanel]);

  // HypeApp トップページでは動画のリサイズモードはクロップ固定
  useEffect(() => {
    setVideoSettings((prev) => (prev.resizeMode === 'crop' ? prev : { ...prev, resizeMode: 'crop' }));
  }, [setVideoSettings]);

  // ナビゲーションガイド (driver.js) を初回マウント時に起動
  // - HypeApp(SAITE) ブランドのみ (features.navigationGuide フラグでガード)
  // - navigation_guide_completed_at が NULL のときだけ自動起動
  // - アンケート(属性収集モーダル)が未完了の間は起動を待つ。ガイドはアンケートの後で出す。
  // - マイページの「再生」から reset → ホームに戻ってきた場合も同条件で再起動する
  const { data: userProfile } = useUserProfile();
  const { start: startGuide } = useNavigationGuide();
  const surveyModalVisible = useSurveyModalStore((s) => s.visible);
  const guideStartedRef = useRef(false);
  useEffect(() => {
    if (!navigationGuideEnabled()) return;
    if (!userProfile) return;
    if (userProfile.navigation_guide_completed_at) return;
    // アンケートが残っている間はガイドを出さない。回答/スキップで userProfile が
    // 更新されると再評価され、未回答が無くなった時点で起動する。
    if (getPendingSurveyQuestion(userProfile)) return;
    // 回答直後は userProfile 上は未回答が解消しても、獲得ポイントの演出で
    // モーダルが数秒残る。閉じ切る (visible=false) まで待ってから起動する。
    if (surveyModalVisible) return;
    if (guideStartedRef.current) return;
    guideStartedRef.current = true;
    // DOM が確実に揃ってから起動
    const id = window.setTimeout(() => startGuide(), 350);
    return () => window.clearTimeout(id);
  }, [userProfile, surveyModalVisible, startGuide]);

  const handleGenerate = () => {
    const trimmed = prompt.trim();
    if (!trimmed) return;
    if (generationType === 'image') {
      setPendingEdit(trimmed);
      navigate('/studio/image/prompt', { state: { initialPrompt: trimmed, autoGenerate: true } });
    } else {
      // 動画は「参照画像モード」で生成する（トップページの詳細設定は VideoGenerationSettings を表示している）
      setVideoGenerationMode('reference');
      setPendingVideoGeneration(trimmed);
      navigate('/studio/video/prompt', { state: { initialPrompt: trimmed, autoGenerate: true } });
    }
  };

  const projectLabel = brandConfig.labels.productShort;
  // main_image_url が設定されていても URL が壊れているケースがある。読み込みに失敗
  // した product.id を覚えておき、サムネをアイコンプレースホルダーにフォールバック。
  const [failedThumbIds, setFailedThumbIds] = useState<Set<string>>(new Set());
  const markThumbFailed = (productId: string) => {
    setFailedThumbIds((prev) => {
      if (prev.has(productId)) return prev;
      const next = new Set(prev);
      next.add(productId);
      return next;
    });
  };

  const { data: videoModels = [] } = useGenerationModels('video');

  const videoModel = videoSettings.videoModel;

  const persistVideoModel = (next: VideoModelType) => {
    setVideoSettings((prev) => ({ ...prev, videoModel: next }));
  };

  // ポイント計算は共通フックに集約（GenerationPreviewPanel と同一ロジック）
  const { imageCredits, videoCredits } = useGenerationCredits();
  const activeCredits = generationType === 'image' ? imageCredits : videoCredits;
  const creditBadgeText = useCreditBadgeText(activeCredits);

  return (
    <div className="hypeapp-home">
      <div className="hypeapp-home-container">
        {(homeHeroTitle || homeHeroSubtitle) && (
          <section className="hypeapp-hero" data-guide-step="hero">
            {homeHeroTitle && (
              <h1 className="hypeapp-hero-title">
                {renderAccentText(homeHeroTitle, { className: 'hypeapp-hero-accent' })}
              </h1>
            )}
            {homeHeroSubtitle && <p className="hypeapp-hero-subtitle">{homeHeroSubtitle}</p>}
          </section>
        )}

        <section className="hypeapp-prompt-section">
          <div
            className={`hypeapp-prompt-card ${isDragOver ? 'hypeapp-prompt-card--drag-over' : ''}`}
            onDragOver={handleCardDragOver}
            onDragEnter={handleCardDragOver}
            onDragLeave={handleCardDragLeave}
            onDrop={handleCardDrop}
          >
            <textarea
              ref={promptRef}
              className="hypeapp-prompt-input"
              data-guide-step="prompt-input"
              placeholder={brandConfig.homePage.promptPlaceholder}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={3}
              autoFocus
            />

            <div className="hypeapp-assets-area">
              {selectedAssets.length > 0 ? (
                <SelectedAssetsPanel
                  assets={selectedAssets}
                  onClearAll={clearAssets}
                  onReorder={reorderAssets}
                />
              ) : (
                <div className="hypeapp-assets-empty-row">
                  <button
                    type="button"
                    className="hypeapp-assets-empty"
                    onClick={() => openPanel('manage')}
                  >
                    <span className="hypeapp-assets-empty-icon">
                      <Paperclip size={20} aria-hidden="true" />
                    </span>
                    <span>
                      <strong>{brandConfig.labels.material}を選択</strong>
                      {/* モバイルはドラッグ&ドロップ不可のため誘導文言を出さない */}
                      {!isMobile && ' またはここにドロップ'}
                    </span>
                  </button>
                  {/* モバイルのみ: カメラ起動ボタン (素材パネルと同じ撮影→アップロード動線) */}
                  {isMobile && (
                    <>
                      <span className="hypeapp-assets-empty-or">or</span>
                      <input
                        ref={cameraInputRef}
                        type="file"
                        accept="image/*"
                        capture="environment"
                        style={{ display: 'none' }}
                        onChange={handleCameraCapture}
                      />
                      <button
                        type="button"
                        className="hypeapp-assets-camera"
                        onClick={() => cameraInputRef.current?.click()}
                        disabled={uploadMutation.isPending}
                        aria-label="カメラで撮影"
                      >
                        <img src="/camera_light.svg" alt="" width={20} height={20} />
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>

            <div className="hypeapp-prompt-bar">
              <div className="hypeapp-type-toggle" data-guide-step="type-toggle">
                <button
                  type="button"
                  className={`hypeapp-type-btn ${generationType === 'image' ? 'hypeapp-type-btn--active' : ''}`}
                  onClick={() => setGenerationType('image')}
                >
                  <span className="hypeapp-type-icon hypeapp-type-icon--image" aria-hidden />
                  静止画
                </button>
                <button
                  type="button"
                  className={`hypeapp-type-btn ${generationType === 'video' ? 'hypeapp-type-btn--active' : ''}`}
                  onClick={() => setGenerationType('video')}
                >
                  <span className="hypeapp-type-icon hypeapp-type-icon--video" aria-hidden />
                  動画
                </button>
              </div>
              <div className="hypeapp-actions">
                {(() => {
                  const generateDisabled = !prompt.trim();
                  const generateButton = (
                    <button
                      type="button"
                      className={`hypeapp-generate-btn hypeapp-generate-btn--${generationType}`}
                      data-guide-step="generate-button"
                      onClick={handleGenerate}
                      disabled={generateDisabled}
                    >
                      <span>生成する</span>
                      <span className="hypeapp-credit-badge">{creditBadgeText}</span>
                    </button>
                  );
                  // 無効時のみツールチップでラップ（ラッパー div 側で hover を拾うため
                  // disabled なボタンでも表示できる）
                  return generateDisabled ? (
                    <Tooltip content="プロンプトを入力してください" placement="top">
                      {generateButton}
                    </Tooltip>
                  ) : (
                    generateButton
                  );
                })()}
              </div>
            </div>

            <button
              type="button"
              className={`hypeapp-detail-divider ${showMediaRequirements ? 'hypeapp-detail-divider--open' : ''}`}
              data-guide-step="detail-settings"
              onClick={() => setShowMediaRequirements((v) => !v)}
              aria-expanded={showMediaRequirements}
            >
              <span className="hypeapp-detail-toggle-label">
                <span className="hypeapp-detail-toggle-chevron" aria-hidden="true">
                  <img src="/horizontal_slider_2.svg" alt="" width="16" height="16" />
                </span>
                詳細設定
              </span>
            </button>

            {showMediaRequirements && (
              <div
                className={`hypeapp-prompt-card-details ${generationType === 'video' ? 'hypeapp-prompt-card-details--video' : ''}`}
              >
                {/* 動画モードのみインラインモデル選択を表示
                    (画像モードは下の MediaRequirementsAccordion が Gemini / GPT を含む選択 UI を描画する)。
                    studio (SetupPanel) と同じ VideoModelFamilySelector を使い、
                    Veo / Seedance / Kling などプロバイダごとに別行で表示する。 */}
                {generationType === 'video' && (
                  <div className="gen-option-group">
                    <span className="gen-option-group-label">モデル</span>
                    <VideoModelFamilySelector
                      models={videoModels}
                      activeModelCode={videoModel}
                      onSelect={(code) => persistVideoModel(code as VideoModelType)}
                    />
                  </div>
                )}
                {generationType === 'image' ? (
                  <MediaRequirementsAccordion
                    isOpen
                    headerless
                    onToggle={() => setShowMediaRequirements((v) => !v)}
                    onClose={() => setShowMediaRequirements(false)}
                    mode="prompt"
                  />
                ) : (
                  <VideoGenerationSettings
                    settings={videoSettings}
                    onSettingsChange={(next) => setVideoSettings({ ...next, resizeMode: 'crop' })}
                    showResizeMode={false}
                    isReferenceImageMode
                    hideVideoModelSelector
                  />
                )}
              </div>
            )}
          </div>
        </section>

        <HomeHeroMenu />

        <section className="hypeapp-recent" data-guide-step="recent-projects">
          <div className="hypeapp-recent-header">
            <h2 className="hypeapp-recent-title">最近の{projectLabel}</h2>
            <button
              type="button"
              className="hypeapp-recent-all"
              onClick={() => navigate('/products/manual')}
            >
              全て見る
              <span className="hypeapp-recent-all-chevron" aria-hidden="true" />
            </button>
          </div>
          <div className="hypeapp-recent-grid">
            <button
              type="button"
              className="hypeapp-recent-card hypeapp-recent-card--new"
              onClick={() => navigate('/products/manual')}
            >
              <span className="hypeapp-recent-card-plus">+</span>
              <span>新規{projectLabel}を登録</span>
            </button>
            {recentProducts.map((product) => {
              const isUntitled = !product.title || product.title === brandConfig.labels.productNewDefault;
              const displayTitle = isUntitled ? `(無題の${projectLabel})` : product.title;
              const hasValidThumb = !!product.main_image_url && !failedThumbIds.has(product.id);
              return (
                <button
                  key={product.id}
                  type="button"
                  className="hypeapp-recent-card hypeapp-recent-card--with-thumb"
                  onClick={() => openWithProductFilter(`manual:${product.id}`, 'shared')}
                >
                  <div className="hypeapp-recent-card-thumb">
                    {hasValidThumb ? (
                      <img
                        src={product.main_image_url ?? undefined}
                        alt=""
                        onError={() => markThumbFailed(product.id)}
                      />
                    ) : (
                      <div className="hypeapp-recent-card-thumb-placeholder">
                        <ImageIcon size={28} aria-hidden="true" />
                      </div>
                    )}
                  </div>
                  <span
                    className={`hypeapp-recent-card-name ${isUntitled ? 'hypeapp-recent-card-name--untitled' : ''}`}
                  >
                    {displayTitle}
                  </span>
                  <span className="hypeapp-recent-card-date">{formatTimeAgo(product.updated_at)}</span>
                </button>
              );
            })}
          </div>
        </section>
      </div>

      {/* 素材パネル展開ボタンは studio (HomePage) と同じ共有コンポーネントを使う */}
      <MaterialsFloatingOpenButton />
    </div>
  );
}

function formatTimeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const hours = Math.floor(diff / (1000 * 60 * 60));
  if (hours < 1) return 'たった今';
  if (hours < 24) return `${hours}時間前`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}日前`;
  return `${Math.floor(days / 30)}ヶ月前`;
}
